import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import { ArrowLeft, Download, Eye, FileText, Mail, Pencil, Phone, Trash2, Upload, User } from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '../components/ui/button';
import api from '../services/api';
import DocumentPreviewDrawer from '../components/DocumentPreviewDrawer';

const StyledCard = styled(Paper)(() => ({
  borderRadius: 16,
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  overflow: 'hidden'
}));

const UI_COLORS = {
  indigo: '#4f46e5',
  green: '#22c55e',
  amber: '#f59e0b',
  red: '#ef4444',
  slate: '#334155'
};

const formatDateIt = (value) => {
  const s = String(value || '').trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return '-';
  return `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}`;
};

const parseFileExtension = (filename) => {
  const name = String(filename || '').trim();
  const idx = name.lastIndexOf('.');
  if (idx === -1) return '';
  const ext = name.slice(idx + 1).trim();
  if (!ext || ext.length > 8) return '';
  if (!/^[a-z0-9]+$/i.test(ext)) return '';
  return ext.toUpperCase();
};

const translateRecessoStato = (value) => {
  const map = { aperto: 'Aperto', in_lavorazione: 'In lavorazione', chiuso: 'Chiuso', annullato: 'Annullato' };
  return map[String(value || '').toLowerCase()] || (value ?? '-');
};

const translateBinarioStato = (value) => {
  const map = { aperto: 'Aperto', in_lavorazione: 'In lavorazione', chiuso: 'Chiuso' };
  return map[String(value || '').toLowerCase()] || (value ?? '-');
};

const translateContrattoEsito = (value) => {
  const map = { recesso_cliente: 'Recesso cliente', risoluzione_contrattuale: 'Risoluzione contrattuale' };
  return map[String(value || '').toLowerCase()] || (value ?? '-');
};

const translateMandatoEsito = (value) => {
  const map = { recesso_cliente: 'Recesso cliente', rinuncia: 'Rinuncia', revoca: 'Revoca' };
  return map[String(value || '').toLowerCase()] || (value ?? '-');
};

const translateRelazioneStato = (value) => {
  const map = { bozza: 'Bozza', inviata: 'Inviata', approvata: 'Approvata' };
  return map[String(value || '').toLowerCase()] || (value ?? '-');
};

const translatePagamenti = (value) => {
  const map = { regolare: 'Regolare', parziale: 'Parziale', insoluto: 'Insoluto' };
  return map[String(value || '').toLowerCase()] || (value ?? '-');
};

const formatMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return n.toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

const normalizeCustomerLabel = (customer) => {
  if (!customer) return '';
  const label = String(customer.full_name || '').trim();
  if (label) return label;
  if (customer.id) return `Cliente #${customer.id}`;
  return '';
};

const normalizeDossierLabel = (dossier) => {
  if (!dossier) return '';
  const ref = String(dossier.reference || '').trim();
  if (ref) return ref;
  if (dossier.id) return `Dossier #${dossier.id}`;
  return '';
};

const getDossierStatusLabel = (status) => {
  if (!status) return '';
  const map = {
    documents_sent: 'Documenti inviati',
    failed: 'Fallito',
    'in negotiation': 'In trattativa',
    processing: 'In lavorazione',
    submitted_quote: 'Preventivo inviato',
    terminated: 'Terminato'
  };
  return map[status] || status;
};

const formatMoneyIt = (value) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n) || n === 0) return '-';
  return n.toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
};

const formatDateTimeIt = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('it-IT');
};

export default function RecessoDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [customer, setCustomer] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editForm, setEditForm] = useState({ id_cliente: '', id_contratto: '' });

  const [customerById, setCustomerById] = useState({});
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerInputValue, setCustomerInputValue] = useState('');

  const [dossierById, setDossierById] = useState({});
  const [dossierOptions, setDossierOptions] = useState([]);
  const [dossierLoading, setDossierLoading] = useState(false);
  const [dossierInputValue, setDossierInputValue] = useState('');

  const [editDatiOpen, setEditDatiOpen] = useState(false);
  const [editDatiSaving, setEditDatiSaving] = useState(false);
  const [editDatiForm, setEditDatiForm] = useState({
    data_comunicazione_recesso: '',
    data_scadenza_preavviso: '',
    canale_comunicazione: 'pec',
    modalita: 'revoca',
    data_comunicazione_studio: '',
    data_preavviso_cliente: '',
    id_motivazione: '',
    motivazione_dettaglio: ''
  });
  const [motivazioni, setMotivazioni] = useState([]);
  const [motivazioniLoading, setMotivazioniLoading] = useState(false);

  const [docUploadOpen, setDocUploadOpen] = useState(false);
  const [docUploadSaving, setDocUploadSaving] = useState(false);
  const [docUploadTipo, setDocUploadTipo] = useState('altro');
  const [docUploadFile, setDocUploadFile] = useState(null);
  const docUploadInputRef = useRef(null);

  const [docDeleteOpen, setDocDeleteOpen] = useState(false);
  const [docDeleteSaving, setDocDeleteSaving] = useState(false);
  const [docDeleteTarget, setDocDeleteTarget] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewRow, setPreviewRow] = useState(null);
  const [previewOpenInNewTabLoading, setPreviewOpenInNewTabLoading] = useState(false);

  const fetchDetail = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/recessi/${id}/detail`);
      const detail = res.data || null;
      setData(detail);

      const customerId = detail?.id_cliente;
      if (customerId) {
        try {
          const c = await api.get(`/gtl-pratiche/customers/by-id/${customerId}`);
          setCustomer(c.data || null);
        } catch (_) {
          setCustomer(null);
        }
      } else {
        setCustomer(null);
      }

      const dossierId = detail?.id_contratto;
      if (dossierId) {
        try {
          const dossierRes = await api.get(`/dossier/${dossierId}`);
          const d = dossierRes.data || null;
          if (d?.id != null) {
            setDossierById((prev) => ({ ...prev, [String(d.id)]: d }));
            setDossierOptions((prev) => {
              const merged = [...prev, d];
              const byId = new Map();
              merged.forEach((x) => {
                if (x?.id) byId.set(String(x.id), x);
              });
              return Array.from(byId.values());
            });
          }
        } catch (_) {}
      }
    } catch (e) {
      setData(null);
      toast.error('Errore nel caricamento del recesso');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
  }, [id]);

  const fetchCustomerById = async (customerId) => {
    const sid = String(customerId || '').trim();
    if (!sid) return null;
    if (customerById[sid]) return customerById[sid];
    try {
      const res = await api.get(`/gtl-pratiche/customers/by-id/${sid}`);
      const c = res.data || null;
      if (c?.id != null) {
        setCustomerById((prev) => ({ ...prev, [String(c.id)]: c }));
        setCustomerOptions((prev) => {
          const merged = [...prev, c];
          const byId = new Map();
          merged.forEach((x) => {
            if (x?.id) byId.set(String(x.id), x);
          });
          return Array.from(byId.values());
        });
      }
      return c;
    } catch (_) {
      return null;
    }
  };

  const fetchDossierById = async (dossierId) => {
    const sid = String(dossierId || '').trim();
    if (!sid) return null;
    if (dossierById[sid]) return dossierById[sid];
    try {
      const res = await api.get(`/dossier/${sid}`);
      const d = res.data || null;
      if (d?.id != null) {
        setDossierById((prev) => ({ ...prev, [String(d.id)]: d }));
        setDossierOptions((prev) => {
          const merged = [...prev, d];
          const byId = new Map();
          merged.forEach((x) => {
            if (x?.id) byId.set(String(x.id), x);
          });
          return Array.from(byId.values());
        });
      }
      return d;
    } catch (_) {
      return null;
    }
  };

  const pickCustomerOption = (idValue) => {
    const sid = String(idValue || '').trim();
    if (!sid) return null;
    return customerOptions.find((c) => String(c?.id) === sid) || customerById[sid] || null;
  };

  const pickDossierOption = (idValue) => {
    const sid = String(idValue || '').trim();
    if (!sid) return null;
    return dossierOptions.find((d) => String(d?.id) === sid) || dossierById[sid] || null;
  };

  useEffect(() => {
    if (!editOpen) return undefined;
    const customerId = String(editForm.id_cliente || '').trim();
    if (customerId && !customerById[customerId]) {
      fetchCustomerById(customerId);
    }
    const dossierId = String(editForm.id_contratto || '').trim();
    if (dossierId && !dossierById[dossierId]) {
      fetchDossierById(dossierId);
    }
    return undefined;
  }, [editOpen, editForm.id_cliente, editForm.id_contratto, customerById, dossierById]);

  useEffect(() => {
    if (!editOpen) {
      setCustomerOptions([]);
      setCustomerInputValue('');
      setDossierOptions([]);
      setDossierInputValue('');
      return undefined;
    }

    const timeoutId = window.setTimeout(async () => {
      const term = String(customerInputValue || '').trim();
      setCustomerLoading(true);
      try {
        const response = await api.get('/gtl-pratiche/customers', {
          params: { search: term || undefined, limit: 20 }
        });
        const list = Array.isArray(response.data?.customers) ? response.data.customers : Array.isArray(response.data) ? response.data : [];
        const current = pickCustomerOption(editForm.id_cliente) ? [pickCustomerOption(editForm.id_cliente)] : [];
        const merged = [...current, ...list];
        const byId = new Map();
        merged.forEach((x) => {
          if (x?.id) byId.set(String(x.id), x);
        });
        const normalized = Array.from(byId.values());
        setCustomerOptions(normalized);
        setCustomerById((prev) => {
          const next = { ...prev };
          normalized.forEach((c) => {
            if (c?.id != null) next[String(c.id)] = c;
          });
          return next;
        });
      } catch (_) {
        setCustomerOptions(pickCustomerOption(editForm.id_cliente) ? [pickCustomerOption(editForm.id_cliente)] : []);
      } finally {
        setCustomerLoading(false);
      }
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [editOpen, customerInputValue, editForm.id_cliente]);

  useEffect(() => {
    if (!editOpen) return undefined;

    const customerId = String(editForm.id_cliente || '').trim();
    if (!customerId) {
      setDossierOptions([]);
      setDossierInputValue('');
      return undefined;
    }

    const timeoutId = window.setTimeout(async () => {
      const term = String(dossierInputValue || '').trim();
      setDossierLoading(true);
      try {
        const response = await api.get(`/dossier/customer/${customerId}`, {
          params: { search: term || undefined, limit: 20 }
        });
        const list = Array.isArray(response.data) ? response.data : [];
        const current = pickDossierOption(editForm.id_contratto) ? [pickDossierOption(editForm.id_contratto)] : [];
        const merged = [...current, ...list];
        const byId = new Map();
        merged.forEach((x) => {
          if (x?.id) byId.set(String(x.id), x);
        });
        const normalized = Array.from(byId.values());
        setDossierOptions(normalized);
        setDossierById((prev) => {
          const next = { ...prev };
          normalized.forEach((d) => {
            if (d?.id != null) next[String(d.id)] = d;
          });
          return next;
        });
      } catch (_) {
        setDossierOptions(pickDossierOption(editForm.id_contratto) ? [pickDossierOption(editForm.id_contratto)] : []);
      } finally {
        setDossierLoading(false);
      }
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [editOpen, editForm.id_cliente, dossierInputValue, editForm.id_contratto]);

  const openEditBase = () => {
    const idCliente = data?.id_cliente != null ? String(data.id_cliente) : '';
    const idContratto = data?.id_contratto != null ? String(data.id_contratto) : '';
    setEditForm({ id_cliente: idCliente, id_contratto: idContratto });
    setCustomerInputValue(customer?.full_name || (idCliente ? `Cliente #${idCliente}` : ''));
    const dossier = pickDossierOption(idContratto);
    setDossierInputValue(dossier ? normalizeDossierLabel(dossier) : idContratto ? `Dossier #${idContratto}` : '');
    setEditOpen(true);
  };

  const closeEditBase = () => {
    if (editSaving) return;
    setEditOpen(false);
  };

  const saveEditBase = async () => {
    if (editSaving) return;
    setEditSaving(true);
    try {
      const id_cliente = editForm.id_cliente ? Number(editForm.id_cliente) : null;
      const id_contratto = editForm.id_contratto ? Number(editForm.id_contratto) : null;
      await api.put(`/recessi/${id}`, { id_cliente, id_contratto });
      await fetchDetail();
      setEditOpen(false);
      toast.success('Aggiornato');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Errore aggiornamento');
    } finally {
      setEditSaving(false);
    }
  };

  useEffect(() => {
    if (!editDatiOpen) {
      setMotivazioni([]);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      setMotivazioniLoading(true);
      try {
        const res = await api.get('/recessi/motivazioni');
        const items = Array.isArray(res.data) ? res.data : [];
        if (!cancelled) setMotivazioni(items);
      } catch (_) {
        if (!cancelled) setMotivazioni([]);
      } finally {
        if (!cancelled) setMotivazioniLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [editDatiOpen]);

  const openEditDati = () => {
    const tipo = String(data?.tipo_iniziativa || '').toLowerCase();
    if (tipo === 'cliente') {
      setEditDatiForm({
        data_comunicazione_recesso: data?.cliente_data_comunicazione_recesso || '',
        data_scadenza_preavviso: data?.cliente_data_scadenza_preavviso || '',
        canale_comunicazione: String(data?.cliente_canale_comunicazione || 'pec'),
        modalita: 'revoca',
        data_comunicazione_studio: '',
        data_preavviso_cliente: '',
        id_motivazione: data?.cliente_id_motivazione != null ? String(data.cliente_id_motivazione) : '',
        motivazione_dettaglio: data?.cliente_motivazione_dettaglio || ''
      });
    } else {
      setEditDatiForm({
        data_comunicazione_recesso: '',
        data_scadenza_preavviso: '',
        canale_comunicazione: 'pec',
        modalita: String(data?.studio_modalita || 'revoca'),
        data_comunicazione_studio: data?.studio_data_comunicazione_studio || '',
        data_preavviso_cliente: data?.studio_data_preavviso_cliente || '',
        id_motivazione: data?.studio_id_motivazione != null ? String(data.studio_id_motivazione) : '',
        motivazione_dettaglio: data?.studio_motivazione_dettaglio || ''
      });
    }
    setEditDatiOpen(true);
  };

  const closeEditDati = () => {
    if (editDatiSaving) return;
    setEditDatiOpen(false);
  };

  const saveEditDati = async () => {
    if (editDatiSaving) return;
    setEditDatiSaving(true);
    try {
      const tipo = String(data?.tipo_iniziativa || '').toLowerCase();
      const payload =
        tipo === 'cliente'
          ? {
              data_comunicazione_recesso: editDatiForm.data_comunicazione_recesso || null,
              data_scadenza_preavviso: editDatiForm.data_scadenza_preavviso || null,
              canale_comunicazione: editDatiForm.canale_comunicazione || null,
              id_motivazione: editDatiForm.id_motivazione ? Number(editDatiForm.id_motivazione) : null,
              motivazione_dettaglio: editDatiForm.motivazione_dettaglio || null
            }
          : {
              modalita: editDatiForm.modalita || null,
              data_comunicazione_studio: editDatiForm.data_comunicazione_studio || null,
              data_preavviso_cliente: editDatiForm.data_preavviso_cliente || null,
              id_motivazione: editDatiForm.id_motivazione ? Number(editDatiForm.id_motivazione) : null,
              motivazione_dettaglio: editDatiForm.motivazione_dettaglio || null
            };

      const endpoint = tipo === 'cliente' ? `/recessi/${id}/cliente` : `/recessi/${id}/studio`;
      const res = await api.put(endpoint, payload);
      setData(res.data || null);
      setEditDatiOpen(false);
      toast.success('Aggiornato');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Errore aggiornamento');
    } finally {
      setEditDatiSaving(false);
    }
  };

  const headerTitle = useMemo(() => {
    const customerName = String(customer?.full_name || '').trim();
    if (customerName) return customerName;
    if (data?.id_cliente) return `Cliente #${data.id_cliente}`;
    return `Recesso #${id}`;
  }, [customer, data, id]);

  const iniziativaLabel = useMemo(() => {
    const t = String(data?.tipo_iniziativa || '').toLowerCase();
    if (t === 'cliente') return 'Recesso del cliente (ipotesi 1)';
    if (t === 'studio') return 'Rinuncia dello studio (ipotesi 2)';
    return '-';
  }, [data]);

  const isCliente = useMemo(() => String(data?.tipo_iniziativa || '').toLowerCase() === 'cliente', [data]);

  const tempisticaLabel = useMemo(() => {
    const t = String(data?.tipo_iniziativa || '').toLowerCase();
    if (t !== 'cliente') return '-';
    return data?.tempistica?.label || 'In attesa delle date';
  }, [data]);

  const motivazioniClienteOptions = useMemo(
    () =>
      (motivazioni || [])
        .filter((m) => ['cliente', 'entrambi'].includes(String(m?.ambito || '').toLowerCase()))
        .sort((a, b) => Number(a?.ordine || 0) - Number(b?.ordine || 0)),
    [motivazioni]
  );

  const motivazioniStudioOptions = useMemo(
    () =>
      (motivazioni || [])
        .filter((m) => ['studio', 'entrambi'].includes(String(m?.ambito || '').toLowerCase()))
        .sort((a, b) => Number(a?.ordine || 0) - Number(b?.ordine || 0)),
    [motivazioni]
  );

  const statoChipColor = useMemo(() => {
    const s = String(data?.stato || '').toLowerCase();
    if (s === 'chiuso') return UI_COLORS.green;
    if (s === 'annullato') return UI_COLORS.red;
    if (s === 'in_lavorazione') return UI_COLORS.amber;
    return UI_COLORS.slate;
  }, [data]);

  const contratoTint = alpha(UI_COLORS.green, 0.1);
  const mandatoTint = alpha(UI_COLORS.amber, 0.1);

  const dossier = useMemo(() => {
    const dossierId = data?.id_contratto != null ? String(data.id_contratto) : '';
    if (!dossierId) return null;
    return dossierById[dossierId] || null;
  }, [data, dossierById]);

  const updateContratto = async (patch) => {
    if (saving) return;
    setSaving(true);
    try {
      const res = await api.put(`/recessi/${id}/binario-contratto`, patch);
      setData(res.data || null);
      toast.success('Aggiornato');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Errore aggiornamento');
    } finally {
      setSaving(false);
    }
  };

  const updateMandato = async (patch) => {
    if (saving) return;
    setSaving(true);
    try {
      const res = await api.put(`/recessi/${id}/binario-mandato`, patch);
      setData(res.data || null);
      toast.success('Aggiornato');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Errore aggiornamento');
    } finally {
      setSaving(false);
    }
  };

  const updateRelazione = async (patch) => {
    if (saving) return;
    setSaving(true);
    try {
      const res = await api.put(`/recessi/${id}/relazione`, patch);
      setData(res.data || null);
      toast.success('Aggiornato');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Errore aggiornamento');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ p: 3 }}>
        <Button variant="outline" onClick={() => navigate('/recessi')}>
          <ArrowLeft size={16} />
          Torna all’elenco
        </Button>
      </Box>
    );
  }

  const docItems = Array.isArray(data.documenti) ? data.documenti : [];

  const openDocUploadDialog = () => {
    setDocUploadTipo('altro');
    setDocUploadFile(null);
    if (docUploadInputRef.current) {
      docUploadInputRef.current.value = '';
    }
    setDocUploadOpen(true);
  };

  const closeDocUploadDialog = () => {
    if (docUploadSaving) return;
    setDocUploadOpen(false);
  };

  const pickDocUploadFile = () => {
    if (docUploadInputRef.current) {
      docUploadInputRef.current.click();
    }
  };

  const onDocUploadFileSelected = (e) => {
    const file = e.target.files?.[0] || null;
    setDocUploadFile(file);
  };

  const uploadDocumento = async () => {
    if (docUploadSaving) return;
    if (!docUploadFile) {
      toast.error('Seleziona un file');
      return;
    }
    setDocUploadSaving(true);
    try {
      const form = new FormData();
      form.append('file', docUploadFile);
      form.append('tipo_documento', docUploadTipo);
      await api.post(`/recessi/${id}/documenti/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Documento caricato');
      setDocUploadOpen(false);
      await fetchDetail();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Errore upload documento');
    } finally {
      setDocUploadSaving(false);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewLoading(false);
    setPreviewUrl('');
    setPreviewRow(null);
  };

  const handleOpenPreview = async (doc) => {
    const sid = String(doc?.id_documento || '').trim();
    if (!sid) return;
    setPreviewRow(doc);
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewUrl('');
    try {
      const res = await api.get(`/recessi/documenti/${sid}/preview-url`);
      const url = res.data?.url;
      if (url) {
        setPreviewUrl(url);
      } else {
        toast.error('URL non disponibile');
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Errore apertura documento');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleOpenPreviewInNewTab = async () => {
    if (previewOpenInNewTabLoading) return;
    const sid = String(previewRow?.id_documento || '').trim();
    if (!sid) return;
    setPreviewOpenInNewTabLoading(true);
    try {
      let url = previewUrl;
      if (!url) {
        const res = await api.get(`/recessi/documenti/${sid}/preview-url`);
        url = res.data?.url || '';
        if (url) setPreviewUrl(url);
      }
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        toast.error('URL non disponibile');
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Errore apertura documento');
    } finally {
      setPreviewOpenInNewTabLoading(false);
    }
  };

  const downloadDocumento = async (docId) => {
    const sid = String(docId || '').trim();
    if (!sid) return;
    try {
      const res = await api.get(`/recessi/documenti/${sid}/download-url`);
      const url = res.data?.url;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        toast.error('URL non disponibile');
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Errore download documento');
    }
  };

  const askDeleteDocumento = (doc) => {
    if (!doc) return;
    setDocDeleteTarget(doc);
    setDocDeleteOpen(true);
  };

  const closeDeleteDocumento = () => {
    if (docDeleteSaving) return;
    setDocDeleteOpen(false);
    setDocDeleteTarget(null);
  };

  const confirmDeleteDocumento = async () => {
    if (docDeleteSaving) return;
    const docId = docDeleteTarget?.id_documento;
    if (!docId) return;
    setDocDeleteSaving(true);
    try {
      await api.delete(`/recessi/documenti/${docId}`);
      toast.success('Documento eliminato');
      setDocDeleteOpen(false);
      setDocDeleteTarget(null);
      await fetchDetail();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Errore eliminazione documento');
    } finally {
      setDocDeleteSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Button variant="outline" onClick={() => navigate('/recessi')}>
          <ArrowLeft size={16} />
          Torna all’elenco
        </Button>
        <Chip
          label={translateRecessoStato(data.stato)}
          size="small"
          variant="outlined"
          sx={{ fontWeight: 800, borderColor: statoChipColor, color: statoChipColor }}
        />
      </Box>

      <StyledCard variant="outlined">
        <Box sx={{ p: 2.5 }}>
          <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 900, letterSpacing: 0.8 }}>PRATICA</Typography>
          <Typography sx={{ fontWeight: 900, fontSize: 34, lineHeight: 1.1, mt: 0.5 }}>{headerTitle}</Typography>
          <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 240 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    bgcolor: '#eef2ff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    color: 'primary.main'
                  }}
                >
                  <User size={16} />
                </Box>
                <Typography sx={{ fontSize: 13, fontWeight: 900 }} noWrap>
                  {customer?.full_name || (data.id_cliente ? `Cliente #${data.id_cliente}` : '-')}
                </Typography>
              </Box>
              {customer?.telephone ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Phone size={14} />
                  <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 800 }} noWrap>
                    {customer.telephone}
                  </Typography>
                </Box>
              ) : null}
              {customer?.email ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Mail size={14} />
                  <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 800 }} noWrap>
                    {customer.email}
                  </Typography>
                </Box>
              ) : null}
              <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 800 }}>
                Contratto (dossier): {data.id_contratto ? dossier?.reference || `#${data.id_contratto}` : '-'}
              </Typography>
              <Grid container spacing={1} sx={{ mt: 0.5 }}>
                <Grid item xs={12} sm={4}>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 800 }}>Responsabile Commerciale</Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 900 }} noWrap>
                    {dossier?.responsabile_commerciale || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 800 }}>Responsabile Upsell</Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 900 }} noWrap>
                    {dossier?.responsabile_upsell || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 800 }}>Responsabile Contabile</Typography>
                  <Typography sx={{ fontSize: 13, fontWeight: 900 }} noWrap>
                    {dossier?.responsabile_contabile || '-'}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="outline" onClick={openEditBase} disabled={saving || editSaving || editDatiSaving}>
                <Pencil size={16} />
                Modifica cliente/contratto
              </Button>
              <Button variant="outline" onClick={openEditDati} disabled={saving || editSaving || editDatiSaving}>
                <Pencil size={16} />
                Modifica motivazione/date
              </Button>
            </Box>
          </Box>

          <Paper elevation={0} variant="outlined" sx={{ mt: 2, borderRadius: 2, p: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 900 }}>INIZIATIVA</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 900 }}>{iniziativaLabel}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 900 }}>MOTIVAZIONE</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 900 }}>{data.motivazione_descrizione || '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 900 }}>TEMPISTICA</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 900, color: tempisticaLabel === 'Tardivo' ? UI_COLORS.red : UI_COLORS.green }}>
                  {tempisticaLabel}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          <Typography sx={{ mt: 2, fontSize: 13, color: 'text.secondary' }}>
            Ogni recesso avanza su due percorsi paralleli e indipendenti — aggiorna lo stato di ciascuno man mano che procede
          </Typography>

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                variant="outlined"
                sx={{ borderRadius: 2, overflow: 'hidden', borderColor: alpha(UI_COLORS.green, 0.45) }}
              >
                <Box sx={{ p: 2, bgcolor: contratoTint, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                  <Box>
                    <Typography sx={{ fontWeight: 900 }}>Società</Typography>
                    <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Contratto di consulenza</Typography>
                  </Box>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <Select
                      value={String(data.contratto_stato || 'aperto')}
                      onChange={(e) => updateContratto({ stato: String(e.target.value || '') })}
                      disabled={saving}
                      sx={{ bgcolor: 'background.paper' }}
                    >
                      <MenuItem value="aperto">Aperto</MenuItem>
                      <MenuItem value="in_lavorazione">In lavorazione</MenuItem>
                      <MenuItem value="chiuso">Chiuso</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ p: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 900 }}>Esito</Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 900 }}>{translateContrattoEsito(data.contratto_esito)}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 900 }}>Stato pagamenti</Typography>
                      <FormControl size="small" fullWidth sx={{ mt: 0.5 }}>
                        <Select
                          value={String(data.contratto_stato_pagamenti || 'regolare')}
                          onChange={(e) => updateContratto({ stato_pagamenti: String(e.target.value || '') })}
                          disabled={saving}
                        >
                          <MenuItem value="regolare">{translatePagamenti('regolare')}</MenuItem>
                          <MenuItem value="parziale">{translatePagamenti('parziale')}</MenuItem>
                          <MenuItem value="insoluto">{translatePagamenti('insoluto')}</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 900 }}>Importo trattenuto</Typography>
                      <TextField
                        size="small"
                        value={formatMoney(data.contratto_importo_trattenuto || 0)}
                        onChange={(e) => updateContratto({ importo_trattenuto: e.target.value })}
                        disabled={saving}
                        fullWidth
                        sx={{ mt: 0.5 }}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">€</InputAdornment>
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                variant="outlined"
                sx={{ borderRadius: 2, overflow: 'hidden', borderColor: alpha(UI_COLORS.amber, 0.55) }}
              >
                <Box sx={{ p: 2, bgcolor: mandatoTint, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                  <Box>
                    <Typography sx={{ fontWeight: 900 }}>Studio Legale</Typography>
                    <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Mandato professionale</Typography>
                  </Box>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <Select
                      value={String(data.mandato_stato || 'aperto')}
                      onChange={(e) => updateMandato({ stato: String(e.target.value || '') })}
                      disabled={saving}
                      sx={{ bgcolor: 'background.paper' }}
                    >
                      <MenuItem value="aperto">Aperto</MenuItem>
                      <MenuItem value="in_lavorazione">In lavorazione</MenuItem>
                      <MenuItem value="chiuso">Chiuso</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ p: 2 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 900 }}>Esito</Typography>
                      <Typography sx={{ fontSize: 14, fontWeight: 900 }}>{translateMandatoEsito(data.mandato_esito)}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 900 }}>Relazione attività</Typography>
                      <FormControl size="small" fullWidth sx={{ mt: 0.5 }}>
                        <Select
                          value={String(data.relazione_stato || 'bozza')}
                          onChange={(e) => updateRelazione({ stato: String(e.target.value || '') })}
                          disabled={saving}
                        >
                          <MenuItem value="bozza">{translateRelazioneStato('bozza')}</MenuItem>
                          <MenuItem value="inviata">{translateRelazioneStato('inviata')}</MenuItem>
                          <MenuItem value="approvata">{translateRelazioneStato('approvata')}</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            </Grid>
          </Grid>

          <Paper elevation={0} variant="outlined" sx={{ mt: 2, borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FileText size={18} />
                <Typography sx={{ fontWeight: 900 }}>Documenti collegati a questo recesso</Typography>
              </Box>
              <Button variant="outline" onClick={openDocUploadDialog} disabled={saving || docUploadSaving || docDeleteSaving}>
                <Upload size={16} />
                Carica documento
              </Button>
            </Box>
            <Box sx={{ px: 2, pb: 2 }}>
              {docItems.length === 0 ? (
                <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Nessun documento caricato per questo recesso.</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {docItems.map((d) => (
                    <Paper key={d.id_documento} elevation={0} variant="outlined" sx={{ borderRadius: 2, p: 1.25 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 900 }} noWrap title={d.nome_file || ''}>
                            {d.nome_file || d.percorso_file || '-'}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                            <Chip
                              label={String(d.tipo_documento || 'altro')}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: 12, height: 22 }}
                            />
                            <Typography sx={{ fontSize: 12, color: 'text.secondary' }} noWrap>
                              {String(d.data_caricamento || '').slice(0, 10)} · {d.caricato_da_name || '-'}
                            </Typography>
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleOpenPreview(d)}
                            disabled={!d.percorso_file || docUploadSaving || docDeleteSaving}
                          >
                            <Eye size={16} />
                            Preview
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadDocumento(d.id_documento)}
                            disabled={!d.percorso_file || docUploadSaving || docDeleteSaving}
                          >
                            <Download size={16} />
                            Scarica
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => askDeleteDocumento(d)}
                            disabled={docUploadSaving || docDeleteSaving}
                          >
                            <Trash2 size={16} />
                            Elimina
                          </Button>
                        </Box>
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      </StyledCard>

      <DocumentPreviewDrawer
        open={previewOpen}
        onClose={handleClosePreview}
        fileName={previewRow?.nome_file || ''}
        fileUrl={previewUrl}
        loading={previewLoading}
        onOpenInNewTab={handleOpenPreviewInNewTab}
        openInNewTabLoading={previewOpenInNewTabLoading}
        onDownload={
          previewRow?.id_documento
            ? () => {
                downloadDocumento(previewRow.id_documento);
              }
            : undefined
        }
        downloadDisabled={!previewRow?.percorso_file}
        details={[
          { label: 'Nome', value: previewRow?.nome_file || '-' },
          { label: 'Tipo', value: String(previewRow?.tipo_documento || 'altro') },
          { label: 'Formato', value: parseFileExtension(previewRow?.nome_file) || '-' },
          { label: 'Caricato', value: String(previewRow?.data_caricamento || '').slice(0, 10) || '-' }
        ]}
      />

      <Dialog
        open={docUploadOpen}
        onClose={closeDocUploadDialog}
        fullWidth
        maxWidth="sm"
        sx={{ '& .MuiDialog-paper': { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 900, pb: 1 }}>Carica documento</DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          <input ref={docUploadInputRef} type="file" hidden onChange={onDocUploadFileSelected} />
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Button variant="outline" size="sm" onClick={pickDocUploadFile} disabled={docUploadSaving}>
                  <Upload size={16} />
                  Scegli file
                </Button>
                <Typography sx={{ fontSize: 13, color: docUploadFile ? 'text.primary' : 'text.secondary' }} noWrap>
                  {docUploadFile?.name || 'Nessun file selezionato'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo documento</InputLabel>
                <Select
                  label="Tipo documento"
                  value={docUploadTipo}
                  onChange={(e) => setDocUploadTipo(String(e.target.value || 'altro'))}
                  disabled={docUploadSaving}
                >
                  <MenuItem value="contratto">Contratto</MenuItem>
                  <MenuItem value="mandato">Mandato</MenuItem>
                  <MenuItem value="comunicazione_recesso">Comunicazione recesso</MenuItem>
                  <MenuItem value="relazione">Relazione</MenuItem>
                  <MenuItem value="altro">Altro</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="ghost" onClick={closeDocUploadDialog} disabled={docUploadSaving}>
            Annulla
          </Button>
          <Button onClick={uploadDocumento} disabled={docUploadSaving || !docUploadFile}>
            Carica
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={docDeleteOpen}
        onClose={closeDeleteDocumento}
        fullWidth
        maxWidth="xs"
        sx={{ '& .MuiDialog-paper': { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ fontWeight: 900, pb: 1 }}>Elimina documento</DialogTitle>
        <DialogContent dividers sx={{ p: 2 }}>
          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
            Confermi l’eliminazione del documento {docDeleteTarget?.nome_file ? `"${docDeleteTarget.nome_file}"` : ''}?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="ghost" onClick={closeDeleteDocumento} disabled={docDeleteSaving}>
            Annulla
          </Button>
          <Button variant="destructive" onClick={confirmDeleteDocumento} disabled={docDeleteSaving}>
            Elimina
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={editDatiOpen} onClose={closeEditDati} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 900 }}>Modifica motivazione e date</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {isCliente ? (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    size="small"
                    type="date"
                    label="Data comunicazione recesso"
                    value={editDatiForm.data_comunicazione_recesso || ''}
                    onChange={(e) => setEditDatiForm((p) => ({ ...p, data_comunicazione_recesso: String(e.target.value || '') }))}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    size="small"
                    type="date"
                    label="Data scadenza preavviso"
                    value={editDatiForm.data_scadenza_preavviso || ''}
                    onChange={(e) => setEditDatiForm((p) => ({ ...p, data_scadenza_preavviso: String(e.target.value || '') }))}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Canale comunicazione</InputLabel>
                    <Select
                      label="Canale comunicazione"
                      value={editDatiForm.canale_comunicazione || 'pec'}
                      onChange={(e) => setEditDatiForm((p) => ({ ...p, canale_comunicazione: String(e.target.value || '') }))}
                    >
                      <MenuItem value="pec">PEC</MenuItem>
                      <MenuItem value="raccomandata">Raccomandata</MenuItem>
                      <MenuItem value="email">Email</MenuItem>
                      <MenuItem value="altro">Altro</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField size="small" label="Tempistica" value={tempisticaLabel} InputProps={{ readOnly: true }} fullWidth />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    size="small"
                    disabled={motivazioniLoading}
                    options={motivazioniClienteOptions}
                    value={motivazioniClienteOptions.find((m) => String(m?.id_motivazione) === String(editDatiForm.id_motivazione || '')) || null}
                    onChange={(_e, v) => setEditDatiForm((p) => ({ ...p, id_motivazione: v?.id_motivazione ? String(v.id_motivazione) : '' }))}
                    getOptionLabel={(o) => String(o?.descrizione || '').trim() || ''}
                    isOptionEqualToValue={(a, b) => String(a?.id_motivazione) === String(b?.id_motivazione)}
                    renderInput={(params) => <TextField {...params} label="Motivazione" placeholder="Cerca motivazione..." />}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    size="small"
                    label="Dettaglio motivazione"
                    value={editDatiForm.motivazione_dettaglio || ''}
                    onChange={(e) => setEditDatiForm((p) => ({ ...p, motivazione_dettaglio: String(e.target.value || '') }))}
                    fullWidth
                    multiline
                    minRows={3}
                    placeholder="Note aggiuntive..."
                  />
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Modalità</InputLabel>
                    <Select
                      label="Modalità"
                      value={editDatiForm.modalita || 'revoca'}
                      onChange={(e) => setEditDatiForm((p) => ({ ...p, modalita: String(e.target.value || '') }))}
                    >
                      <MenuItem value="revoca">Revoca del mandato</MenuItem>
                      <MenuItem value="rinuncia">Rinuncia al mandato</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    size="small"
                    type="date"
                    label="Data comunicazione studio"
                    value={editDatiForm.data_comunicazione_studio || ''}
                    onChange={(e) => setEditDatiForm((p) => ({ ...p, data_comunicazione_studio: String(e.target.value || '') }))}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    size="small"
                    type="date"
                    label="Data preavviso concesso al cliente"
                    value={editDatiForm.data_preavviso_cliente || ''}
                    onChange={(e) => setEditDatiForm((p) => ({ ...p, data_preavviso_cliente: String(e.target.value || '') }))}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    size="small"
                    disabled={motivazioniLoading}
                    options={motivazioniStudioOptions}
                    value={motivazioniStudioOptions.find((m) => String(m?.id_motivazione) === String(editDatiForm.id_motivazione || '')) || null}
                    onChange={(_e, v) => setEditDatiForm((p) => ({ ...p, id_motivazione: v?.id_motivazione ? String(v.id_motivazione) : '' }))}
                    getOptionLabel={(o) => String(o?.descrizione || '').trim() || ''}
                    isOptionEqualToValue={(a, b) => String(a?.id_motivazione) === String(b?.id_motivazione)}
                    renderInput={(params) => <TextField {...params} label="Motivazione" placeholder="Cerca motivazione..." />}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    size="small"
                    label="Dettaglio motivazione"
                    value={editDatiForm.motivazione_dettaglio || ''}
                    onChange={(e) => setEditDatiForm((p) => ({ ...p, motivazione_dettaglio: String(e.target.value || '') }))}
                    fullWidth
                    multiline
                    minRows={3}
                    placeholder="Note aggiuntive..."
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="ghost" onClick={closeEditDati} disabled={editDatiSaving}>
            Annulla
          </Button>
          <Button onClick={saveEditDati} disabled={editDatiSaving}>
            {editDatiSaving ? 'Salvo...' : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={editOpen}
        onClose={closeEditBase}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            borderRadius: 16,
            padding: 8
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>Modifica cliente e contratto</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Autocomplete
              size="small"
              options={customerOptions}
              value={pickCustomerOption(editForm.id_cliente)}
              loading={customerLoading}
              onChange={(_e, v) => {
                const nextCustomerId = v?.id ? String(v.id) : '';
                setEditForm((p) => ({ ...p, id_cliente: nextCustomerId, id_contratto: '' }));
                setDossierOptions([]);
                setDossierInputValue('');
                if (v?.id != null) {
                  setCustomerById((prev) => ({ ...prev, [String(v.id)]: v }));
                  setCustomerOptions((prev) => {
                    const merged = [...prev, v];
                    const byId = new Map();
                    merged.forEach((c) => {
                      if (c?.id) byId.set(String(c.id), c);
                    });
                    return Array.from(byId.values());
                  });
                  setCustomerInputValue(v.full_name || '');
                } else {
                  setCustomerInputValue('');
                }
              }}
              onInputChange={(_event, value) => setCustomerInputValue(value)}
              inputValue={customerInputValue}
              filterOptions={(options) => options}
              isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
              getOptionLabel={(option) => normalizeCustomerLabel(option)}
              renderOption={(props, option) => (
                <Box component="li" {...props} sx={{ py: 1, alignItems: 'flex-start' }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      bgcolor: '#eef2ff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      color: 'primary.main',
                      mt: 0.25
                    }}
                  >
                    <User size={18} />
                  </Box>
                  <Box sx={{ ml: 1.5, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>
                      {normalizeCustomerLabel(option)}
                    </Typography>
                    {option?.telephone ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
                        <Phone size={14} />
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {option.telephone}
                        </Typography>
                      </Box>
                    ) : null}
                    {option?.email ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
                        <Mail size={14} />
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {option.email}
                        </Typography>
                      </Box>
                    ) : null}
                  </Box>
                </Box>
              )}
              renderInput={(params) => <TextField {...params} label="Cliente" placeholder="Nome cliente..." />}
            />
            <Autocomplete
              size="small"
              disabled={!String(editForm.id_cliente || '').trim()}
              options={dossierOptions}
              value={pickDossierOption(editForm.id_contratto)}
              loading={dossierLoading}
              onChange={(_e, v) => {
                const nextId = v?.id ? String(v.id) : '';
                setEditForm((p) => ({ ...p, id_contratto: nextId }));
                if (v?.id != null) {
                  setDossierById((prev) => ({ ...prev, [String(v.id)]: v }));
                  setDossierOptions((prev) => {
                    const merged = [...prev, v];
                    const byId = new Map();
                    merged.forEach((d) => {
                      if (d?.id) byId.set(String(d.id), d);
                    });
                    return Array.from(byId.values());
                  });
                  setDossierInputValue(normalizeDossierLabel(v));
                } else {
                  setDossierInputValue('');
                }
              }}
              onInputChange={(_event, value) => setDossierInputValue(value)}
              inputValue={dossierInputValue}
              filterOptions={(options) => options}
              isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
              getOptionLabel={(option) => normalizeDossierLabel(option)}
              renderOption={(props, option) => (
                <Box component="li" {...props} sx={{ py: 1, alignItems: 'flex-start' }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 900 }} noWrap>
                        {option.reference || `Dossier #${option.id}`}
                      </Typography>
                      {option.status ? (
                        <Chip
                          label={getDossierStatusLabel(option.status)}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem', fontWeight: 800 }}
                        />
                      ) : null}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5, flexWrap: 'wrap' }}>
                      <Typography variant="body2" sx={{ fontWeight: 800, color: UI_COLORS.green }}>
                        {formatMoneyIt(option.importo_totale)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 800 }}>
                        Acconto: {formatMoneyIt(option.acconto)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 800 }}>
                        {option.data_conferimento ? formatDateTimeIt(option.data_conferimento) : '-'}
                      </Typography>
                      {option.tipo_contratto ? (
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 800 }}>
                          {option.tipo_contratto}
                        </Typography>
                      ) : null}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 800 }} noWrap>
                      {`Comm: ${option.responsabile_commerciale || '-'} · Upsell: ${option.responsabile_upsell || '-'} · Contabile: ${
                        option.responsabile_contabile || '-'
                      }`}
                    </Typography>
                    {option.description ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 520 }} noWrap>
                        {option.description}
                      </Typography>
                    ) : null}
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Contratto (Dossier)"
                  placeholder={String(editForm.id_cliente || '').trim() ? 'Cerca dossier...' : 'Seleziona prima un cliente'}
                  helperText={
                    pickDossierOption(editForm.id_contratto) ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                            {formatMoneyIt(pickDossierOption(editForm.id_contratto)?.importo_totale)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                            Acconto: {formatMoneyIt(pickDossierOption(editForm.id_contratto)?.acconto)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                            {pickDossierOption(editForm.id_contratto)?.data_conferimento
                              ? formatDateTimeIt(pickDossierOption(editForm.id_contratto).data_conferimento)
                              : '-'}
                          </Typography>
                          {pickDossierOption(editForm.id_contratto)?.status ? (
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
                              {getDossierStatusLabel(pickDossierOption(editForm.id_contratto).status)}
                            </Typography>
                          ) : null}
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }} noWrap>
                          {`Comm: ${pickDossierOption(editForm.id_contratto)?.responsabile_commerciale || '-'} · Upsell: ${
                            pickDossierOption(editForm.id_contratto)?.responsabile_upsell || '-'
                          } · Contabile: ${pickDossierOption(editForm.id_contratto)?.responsabile_contabile || '-'}`}
                        </Typography>
                      </Box>
                    ) : (
                      'Lookup remoto su dossier filtrato per cliente'
                    )
                  }
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="ghost" onClick={closeEditBase} disabled={editSaving}>
            Annulla
          </Button>
          <Button onClick={saveEditBase} disabled={editSaving || !String(editForm.id_cliente || '').trim()}>
            {editSaving ? 'Salvataggio...' : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
